import { MainContentBoxName, StaticProfileConfig } from '@lemontaps/lib/types/api';
import BioTextBox from './sections/BioTextBox';
import ContactDetailsBox from './sections/ContactDetailsBox';
import ContactFormBox from './sections/ContactFormBox';
import Footer from './sections/Footer';
import TryForFreeBanner from './sections/TryForFreeBanner';
import UrlsBox from './sections/UrlsBox';
import ProfileThemeProvider from '../ProfileThemeProvider';
import ResponsiveProfileWidthContainer from './components/ResponsiveProfileContainer';
import Banner from './sections/Banner';
import HeaderBox from './sections/HeaderBox';
import { Box, Card } from '@mui/material';
import { ProfileEditProps, TrackHandlers } from './types';
import { Stack } from '@mui/system';
import FontWrapper from './components/FontWrapper';
import { assertUnreachable } from '@lemontaps/lib/types/util';
import TeamDirectoryBox from './sections/TeamDirectoryBox';
import CustomCssInjector from './components/CustomCssWrapper';
import { customCssClasses } from '@lemontaps/lib/constants/profile-page';

// todo: integrate with /edit-bio

type Props = {
  profile: StaticProfileConfig;
  onContactFormSubmit?: (values: Record<string, string>) => Promise<boolean>;
  renderTryForFreeBanner?: boolean; // defaults to false
  renderFooter?: boolean; // defaults to true
  renderCookieSettings?: boolean; // defaults to false, only relevant if footer is rendered
  suppressLeadGen?: boolean; // defaults to false, only relevant if profile.leadGen is provided
  suppressLeadGenPopupFlow?: boolean; // defaults to false, only relevant if profile.leadGen is provided and suppressLeaGen is false
  editProps?: ProfileEditProps;
  trackHandlers?: TrackHandlers;
  renderBoxes?: Array<BoxName>; // override info about which boxes to render. Relevant when embedding the profile UI somehwere.
};
type BoxName = 'header' | MainContentBoxName;

export default function StaticProfile({
  profile,
  onContactFormSubmit,
  renderTryForFreeBanner = false,
  renderFooter = true,
  renderCookieSettings = false,
  suppressLeadGen = false,
  suppressLeadGenPopupFlow = false,
  editProps,
  trackHandlers = {},
  renderBoxes,
}: Props) {
  const {
    contactSaveTrackHandler,
    linkClickTrackHandler,
    fileClickTrackHandler,
    contactSubmitTrackHandler,
    profileImageClickTrackHandler,
  } = trackHandlers;

  const shouldHide = (name: BoxName) => renderBoxes && !renderBoxes.includes(name);

  return (
    <>
      <CustomCssInjector customCss={profile.config.customCss} />
      <ProfileThemeProvider metaTheme={profile.config.metaTheme}>
        <FontWrapper font={profile.config.metaTheme.font}>
          <Box className={customCssClasses.globalElements.pageWrapper.className}>
            <ResponsiveProfileWidthContainer
              className={customCssClasses.globalElements.pageContainer.className}
            >
              {renderTryForFreeBanner && <TryForFreeBanner username={profile.meta.username} />}
              <Card
                className={customCssClasses.globalElements.pageContainerCard.className}
                sx={theme => ({
                  pb: 2,
                  px: 0,
                  mb: 8,
                  borderTopRightRadius: 0,
                  borderTopLeftRadius: 0,
                  [theme.breakpoints.down('md')]: {
                    my: 0,
                    borderRadius: 0,
                    border: 'none',
                    pb: 8,
                  },
                })}
              >
                {!shouldHide('header') && (
                  <Banner
                    bannerImgUrl={profile.header.bannerImageUrl}
                    editProps={editProps?.bannerImageEditProps}
                    renderBottomInset
                  />
                )}
                <Stack spacing={2} px={{ xs: 1, md: 2 }}>
                  {!shouldHide('header') && (
                    <HeaderBox
                      config={profile.header}
                      profileImageEditProps={editProps?.profileImageEditProps}
                      logoImageEditProps={editProps?.logoImageEditProps}
                      trackHandlers={{ contactSaveTrackHandler, profileImageClickTrackHandler }}
                    />
                  )}
                  {shouldHide('header') && <Box height={4} />}
                  {profile.body.boxes.map(boxConfig => {
                    if (shouldHide(boxConfig.name)) return null;
                    switch (boxConfig.name) {
                      case 'contactDetailsBox':
                        return (
                          <ContactDetailsBox
                            config={boxConfig}
                            editProps={editProps?.contactDetailsBoxEditProps}
                          />
                        );
                      case 'customBioTextBox':
                        return (
                          <BioTextBox
                            config={boxConfig}
                            editProps={editProps?.customBioTextEditProps}
                          />
                        );
                      case 'urlsBox_links':
                        return (
                          <UrlsBox
                            config={boxConfig}
                            editProps={editProps?.linksBoxEditProps}
                            trackHandler={linkClickTrackHandler}
                            containerClassName={customCssClasses.linksBox.container.className}
                            titleClassName={customCssClasses.linksBox.title.className}
                          />
                        );
                      case 'urlsBox_files':
                        return (
                          <UrlsBox
                            config={boxConfig}
                            editProps={editProps?.filesBoxEditProps}
                            trackHandler={fileClickTrackHandler}
                            containerClassName={customCssClasses.filesBox.container.className}
                            titleClassName={customCssClasses.filesBox.title.className}
                          />
                        );
                      case 'teamDirectoryBox':
                        return <TeamDirectoryBox config={boxConfig} />;
                      case 'contactFormBox':
                        if (!profile.leadGen || suppressLeadGen) return null;
                        return (
                          <ContactFormBox
                            config={profile.leadGen}
                            font={profile.config.metaTheme.font}
                            onSubmit={onContactFormSubmit}
                            suppressPopupFlow={suppressLeadGenPopupFlow}
                            trackHandlers={{ contactSubmitTrackHandler }}
                            isEdit={!!editProps}
                          />
                        );
                      default: // if error, then a case is missing
                        assertUnreachable(boxConfig);
                    }
                  })}
                  {renderFooter && (
                    <Footer
                      config={profile.footer}
                      supportedLangs={profile.meta.supportedLangs}
                      renderCookieSettings={renderCookieSettings}
                    />
                  )}
                </Stack>
              </Card>
            </ResponsiveProfileWidthContainer>
          </Box>
        </FontWrapper>
      </ProfileThemeProvider>
    </>
  );
}
